import { HTML5toTouch } from "rdndmb-html5-to-touch";
import { useEffect, useRef, useState } from "react";
import { DndProvider } from "react-dnd-multi-backend";
import "../App.css";
import { DraggableMessage } from "../components/Draggables";
import InputBox from "../components/InputBox";
import {
  COLOR_DIVISOR,
  COLOR_HIGHLIGHTED,
  FONT,
  REGEX_VALID_DECIMAL,
  ROMAN_FONT,
  ROMAN_SYSTEM
} from "../defaults";
import { convertToAlternate, increaseProgress } from "../helpers";
import coin_C from "../images/coin_C.png";
import coin_D from "../images/coin_D.png";
import coin_I from "../images/coin_I.png";
import coin_L from "../images/coin_L.png";
import coin_M from "../images/coin_M.png";
import coin_V from "../images/coin_V.png";
import coin_X from "../images/coin_X.png";
import {
  buttonCheck,
  buttonCheckLine,
  InputBoxRemainder,
  lang,
  levelTableDivisionAction,
  levelDivisionTask1,
  levelDivisionTask2,
  levelCorrect,
  levelIncorrect,

  levelNoInputMulti,
  levelTableDivisionSolveGridFirst,
  levelTableDivisionTitleLeft,
  levelTableDivisionTitleRight
} from "../strings";

const LevelNumber = 5;

function calculateDividend(currentProgress) {
  const numbers = [2083, 1353, 4600];
  if (currentProgress >= numbers.length) {
    return Math.floor(Math.random() * 4000) + 1000; // Range 1000-4999. Larger numbers become VERY long in Roman representation.
  }
  return numbers[currentProgress];
}

function calculateDivisor(currentProgress) {
  const numbers = [2, 5, 3];
  if (currentProgress >= numbers.length) {
    return Math.floor(Math.random() * 8) + 2;
  }
  return numbers[currentProgress];
}

const coinImages = {
  0: coin_M,
  1: coin_D,
  2: coin_C,
  3: coin_L,
  4: coin_X,
  5: coin_V,
  6: coin_I,
};

// Number of rows is variable, depending on setup. Number of columns is always fixed.
const cols = 7; 
const rows = 13; // Has to be large enough to fit all possible rows, so make it too large rather than too small. 

function Coin({ coinImage }) {
  return (
    <div
      style={{
        maxWidth: "4dvh",
        maxHeight: "4dvh",
      }}
    >
      <img
        src={coinImage}
        alt="coin"
        style={{
          width: "100%",
          height: "100%",
          filter: "drop-shadow(0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5))",
        }}
      />
    </div>
  );
}

function Level5Roman({levelProgress, setLevelProgress, showMessage, setShowMessage, disableInput, setDisableInput}) {
  const [dividend, setDividend] = useState(calculateDividend(levelProgress[LevelNumber]));
  const [divisor, setDivisor] = useState(calculateDivisor(levelProgress[LevelNumber]));
  const [lineCount, setLineCount] = useState(1); // Current line is lineCount-1. Line that currently allows input. Maximum is solutionLeft.length + 1.
  const [inputsLeft, setInputsLeft] = useState(
    Array.from({ length: rows }, () => Array.from({ length: cols }, () => "")) // Assuming coins up to Roman M are used
  );
  const [inputsRight, setInputsRight] = useState(
    Array.from({ length: rows }, () => Array.from({ length: cols }, () => "")) // Assuming coins up to Roman M are used
  );
  const [solutionLeft, setSolutionLeft] = useState([]); // Array of arrays. Each element is an array corresponding to the columns of a particular row. That array contains the remainders for each column. Has as many entries (=rows) as there are steps needed for the current exercise. Length changes for each new exercise.
  const [solutionRight, setSolutionRight] = useState([]); // Array of arrays. Each element is an array corresponding to the columns of a particular row. That array contains the quotients for each column.
  const [solutionMiddle, setSolutionMiddle] = useState([]); // Array of arrays. In each such array, first element is 0 or 1, indicating whether performed action was division or exchange, respectively. Other elements contain information on column, current value in left table, quotient and remainder.
  const [highlightedInput, setHighlightedInput] = useState([]);
  const [lockedInputs, setLockedInputs] = useState([]); // Which input fields to disable input for.
  const [initialLeft, setInitialLeft] = useState([]); // Array containing the numbers to be displayed in the left table initially.
  const [correct, setCorrect] = useState(false);

  useEffect(() => {

    // Pre-calculate solutions for both tables.
    const romanString = convertToAlternate(ROMAN_SYSTEM, dividend); 
    let freq =  new Array(cols).fill(0);
    
    for (const char of romanString) { // Calculates char frequencies in romanString and puts them into sorted array. LLM-generated for-loop. 
      if ('MDCLXVI'.includes(char)) {
        freq[['M', 'D', 'C', 'L', 'X', 'V', 'I'].indexOf(char)]++;
      }
    }
    setInitialLeft([...freq]);
    let rem = [...freq];
    let sol = new Array(cols).fill(0);
    let calcLeft = []; // Has as many entries (=rows) as there are steps needed for the current exercise. Length changes for each new exercise.
    let calcRight = [];
    let calcMiddle = [];
    for (let i = 0; i < cols; i++) {
      if (rem[i] >= divisor) {
        calcMiddle = [
          ...calcMiddle,
          [0, rem[i], parseInt(rem[i] / divisor), rem[i] % divisor], // [WHICH_ACTION, WHICH_VALUE, WHICH_QUOTIENT, WHICH_REMAINDER]
        ];
        sol[i] = parseInt(rem[i] / divisor);
        rem[i] = rem[i] % divisor;
        calcLeft = [...calcLeft, Array.from(rem)];
        calcRight = [...calcRight, Array.from(sol)];
      }
      if (rem[i] > 0 && i < (cols-1)) {
        calcMiddle = [...calcMiddle, [1, rem[i], i]]; // [WHICH_ACTION, WHICH_VALUE, WHICH_COLUMN]
        let factor = 2; 
        if (i % 2 === 1) factor = 5; // Depending on parity of i, factor is 2 or 5. E.g. i=0 means coin M, and M=2*D, i=1 means coin D, but D=5*C.  
        rem[i + 1] += rem[i] * factor;
        rem[i] = 0;
        calcLeft = [...calcLeft, Array.from(rem)];
        calcRight = [...calcRight, Array.from(sol)];
      }
    }
    setSolutionLeft(calcLeft);
    setSolutionRight(calcRight);
    setSolutionMiddle(calcMiddle);

    // Calculate and set the pre-filled fields.
    let curLeft = [...freq];
    let curRight =  new Array(cols).fill(0);
    let newInputsLeft = Array.from({ length: rows }, () => // Which values to fill into the disabled input fields.
      Array.from({ length: cols }, () => "")
    );
    let newInputsRight = Array.from({ length: rows }, () =>
      Array.from({ length: cols }, () => "")
    );

    let newLockedInputs = []; // Which input fields to disable.
    for (let i = 0; i < calcLeft.length; i++) {
      for (let j = 0; j < cols; j++) {
        if (calcLeft[i][j] === curLeft[j]) { // The fields that are not changed in this row compared to the previous row above can be disabled.
          newInputsLeft[i][j] = curLeft[j];
          newLockedInputs = [...newLockedInputs, [0, i, j]];
        }
        if (calcRight[i][j] === curRight[j]) {
          newInputsRight[i][j] = curRight[j];
          newLockedInputs = [...newLockedInputs, [1, i, j]];
        }
      }
      curLeft = calcLeft[i];
      curRight = calcRight[i];
    }
    setInputsLeft(newInputsLeft);
    setInputsRight(newInputsRight);
    setLockedInputs(newLockedInputs);
  }, [dividend, divisor]);

  useEffect(() => {
    setDividend(calculateDividend(levelProgress[LevelNumber]));
    setDivisor(calculateDivisor(levelProgress[LevelNumber]));
    setLineCount(1);
  }, [levelProgress]);

  const [message, setMessage] = useState("");

  const handleCorrect = () => {
    setCorrect(true);
    setMessage(levelCorrect)
    displayMessage();
  };

  const handleIncorrect = () => {
    const inputsComplete =
      solutionLeft.length <= lineCount && solutionRight.length <= lineCount;
    setMessage(levelIncorrect + (inputsComplete ? "" : levelTableDivisionSolveGridFirst));
    displayMessage();
  };

  const handleNoInput = () => {
    setMessage(levelNoInputMulti);
    displayMessage();
  };

  const handleCloseMessage = () => {
    if (correct) {
      increaseProgress(levelProgress, setLevelProgress, LevelNumber, ROMAN_SYSTEM);
    }
    setDisableInput(false);
    setCorrect(false);
  };

  const displayMessage = () => {
    setDisableInput(true);
    setShowMessage(true);
  };

  const handleChangeLeft = (id, id2) => (e) => {
    e.target.value = e.target.value.replace(/\s/g, ''); // LLM-generated. Disallow white-space input.
    const newInputs = [...inputsLeft];
    newInputs[id][id2] = e.target.value;
    setInputsLeft(newInputs);
  };

  const handleChangeRight = (id, id2) => (e) => {
    e.target.value = e.target.value.replace(/\s/g, ''); // LLM-generated. Disallow white-space input.
    const newInputs = [...inputsRight];
    newInputs[id][id2] = e.target.value;
    setInputsRight(newInputs);
  };

  const checkLine = () => {
    let isCorrect = true;
    let newHighlighted = [];
    for (let i = 0; i < cols; i++) { // i is column index.
      if (
        parseInt(inputsLeft[lineCount - 1][i]) !==
        parseInt(solutionLeft[lineCount - 1][i]) || 
        ((parseInt(solutionLeft[lineCount - 1][i]) !== 0 && inputsLeft[lineCount - 1][i].toString().startsWith("0")) || (parseInt(solutionLeft[lineCount - 1][i]) === 0 && inputsLeft[lineCount - 1][i].length > 1)) // Inputs are not allowed to start with 0, unless answer is 0, but then the input must be of length 1 i.e. only one 0 and not multiple.
      ) {
        isCorrect = false;
        const newInputs = [...inputsLeft];
        newInputs[lineCount - 1][i] = "";
        newHighlighted = [...newHighlighted, [0, lineCount - 1, i]];
        setInputsLeft(newInputs);
      }
      if (
        parseInt(inputsRight[lineCount - 1][i]) !==
        parseInt(solutionRight[lineCount - 1][i]) ||
        ((parseInt(solutionRight[lineCount - 1][i]) !== 0 && inputsRight[lineCount - 1][i].toString().startsWith("0")) || (parseInt(solutionRight[lineCount - 1][i]) === 0 && inputsRight[lineCount - 1][i].length > 1)) // Inputs are not allowed to start with 0, unless answer is 0, but then the input must be of length 1 i.e. only one 0 and not multiple.
      ) {
        isCorrect = false;
        const newInputs = [...inputsRight];
        newInputs[lineCount - 1][i] = "";
        newHighlighted = [...newHighlighted, [1, lineCount - 1, i]];
        setInputsRight(newInputs);
      }
    }
    if (isCorrect) {
      setLineCount(lineCount + 1);
    }
    setHighlightedInput(newHighlighted);
    setTimeout(() => {
      setHighlightedInput([]);
    }, 4000);
  };

  const maxBoxSize = 5; // How large cells are in the beginning, they are then later scaled down if table gets too big. In dvh.
  const tableMaxHeight = 40; // How large a table is allowed to become. In dvh.
  let dynamicBoxSize = Math.min(maxBoxSize, tableMaxHeight/(lineCount)); // Used to scale the cells. Cells are of size maxBoxSize until lineCount gets too big, then they get smaller every step.
  
  useEffect( () => {
    dynamicBoxSize = Math.min(maxBoxSize, tableMaxHeight/(lineCount))
  }, [lineCount]);

  const inputStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  fontFamily: FONT,
  color: "white",
  fontSize: (dynamicBoxSize/2)+"dvh",
  textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.6)",
    textAlign: "center",
    width: "25%",
    padding: "2px",
    borderRadius: "0em" // Without this, grid edges on tablets are rounded.
  };

  const getBorderStyle = (rowIndex, colIndex) => {
    const borderTop = rowIndex === 0 ? "0.25dvh solid black" : "none";
    const borderBottom = "0.25dvh solid black";
    const borderLeft = colIndex === 0 ? "0.25dvh solid black" : "none";
    const borderRight = "0.25dvh solid black";
    let isTopLeft = rowIndex === 0 && colIndex === 0;
    let isTopRight = rowIndex === 0 && colIndex === cols-1;
    let isBottomLeft = rowIndex === lineCount && colIndex === 0;
    let isBottomRight = rowIndex === lineCount && colIndex === cols-1;
    const borderRadius= "0em"; // Without this, grid edges don't behave as expected.
    const borderTopLeftRadius = isTopLeft ? "0.25em" : "0em"; // Make corners of grid rounded.
    const borderTopRightRadius = isTopRight ? "0.25em" : "0em";
    const borderBottomLeftRadius = isBottomLeft ? "0.35em" : "0em"; // For some reason, visually it looks the same as the top radii even though it is supposed to be larger.
    const borderBottomRightRadius = isBottomRight ? "0.35em" : "0em";

    return {
      borderTop,
      borderBottom,
      borderLeft,
      borderRight,
      borderRadius,
      borderTopLeftRadius,
      borderTopRightRadius,
      borderBottomLeftRadius,
      borderBottomRightRadius,
    };
  };
  
  const inputBoxRef = useRef();

  // LLM-generated function.
  const callCheckInput = () => {
    // Call the handleAction function in the child via the ref
    if (inputBoxRef.current) {
      inputBoxRef.current.checkInput();
    }
  };
  const dividendLength = initialLeft.reduce((acc, num) => acc + num, 0); // LLM-generated reduction. Used to scale fontSize of InputBox inversely to number of chars in dividend.
  return (
    <div>
      <DndProvider options={HTML5toTouch}>
      {showMessage && <DraggableMessage show={showMessage} setShow={setShowMessage} handleClose={handleCloseMessage} message={message}/>} {/* Conditionally rendering it with showMessage makes the message re-render, and hence center itself, every time. */}
      </DndProvider>
      <div
        className="Welcome-label"
        style={{ fontFamily: FONT, fontSize: "3.5dvh" }}
      >
        {levelDivisionTask1}
        <span style={{fontFamily: ROMAN_FONT, color: COLOR_HIGHLIGHTED, fontSize: "4dvh"}}>{convertToAlternate(ROMAN_SYSTEM, dividend)}</span>
        {levelDivisionTask2}
        <span style={{ color: COLOR_DIVISOR }}>{divisor}</span>?
      </div>

      <div
        className="container"
        style={{ display: "flex", justifyContent: "space-around" }}
      >

      
        <div
          style={{
            display: "flex",
            maxHeight: `${
              (Math.min(lineCount, solutionLeft.length) + 2) * dynamicBoxSize
            }dvh`,
            maxWidth: "50dvh",
            justifyContent: "center",
          }}
        >

          <div
            className="table-division-table"
            style={{
              display: "flex",
              width: `${cols * dynamicBoxSize}dvh`,
              flexDirection: "column",
              boxShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
              borderRadius: "0.25em",
              position: "relative",
            }}
          >
            <div style={{position: "absolute", fontFamily: FONT, fontSize: "2dvh", textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.7)", left: "0.25dvh", top: "-2.5dvh"}}>
              {levelTableDivisionTitleLeft}
            </div>

            {/* Coin Images Row */}
            <div
              style={{
                display: "flex",
                height: dynamicBoxSize+"dvh",
                width: `${cols * dynamicBoxSize}dvh`,
              }}
            >
              {Array.from({ length: cols }).map((_, id) => (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    ...getBorderStyle(0, id),
                    height: dynamicBoxSize+"dvh",
                    width: dynamicBoxSize+"dvh",
                  }}
                >
                  <Coin key={[id, 0]} id={[id, 0]} coinImage={coinImages[id]} />
                </div>
              ))}
            </div>

            <div
              style={{
                display: "flex",
                height: dynamicBoxSize+"dvh",
                width: `${cols * dynamicBoxSize}dvh`,
              }}
            >
              {Array.from({ length: cols }).map((_, id) => (
                <div style={{ ...inputStyle, ...getBorderStyle(0, id) }}>
                  {initialLeft[id]}
                </div>
              ))}
            </div>
            {Array.from({
              length: Math.min(lineCount, solutionLeft.length),
            }).map((_, id) => (
              <div
                style={{
                  display: "flex",
                  height: dynamicBoxSize+"dvh",
                  width: `${cols * dynamicBoxSize}dvh`,
                }}
              >
                {Array.from({ length: cols }).map((_, id2) => (
                  <input
                    className="input-field-table-division"
                    type="text"
                    maxLength={3}
                    value={inputsLeft[id][id2]}
                    onChange={handleChangeLeft(id, id2)}
                    style={{
                      ...inputStyle,
                      ...getBorderStyle(id + 1, id2),
                      backgroundColor: highlightedInput.every(
                        (v) => v[0] !== 0 || v[1] !== id || v[2] !== id2 // Returns true if highlightedInput has no entry with the current coordinates for the left table. If a single entry with current coords exists, then field is highlighted.
                      )
                        ? undefined
                        : "rgb(252, 111, 111)",
                    }}
                    placeholder=""
                    pattern={REGEX_VALID_DECIMAL}
                    disabled={
                      id + 1 !== lineCount || // True for all but last row that is being displayed. So all earlier rows are disabled.
                      lockedInputs.some(
                        (v) => v[0] === 0 && v[1] === id && v[2] === id2 // Returns true if lockedInputs has an entry with the current coordinates for the left table.
                      ) ||
                      disableInput
                    }
                  ></input>
                ))}
              </div>
            ))}
          </div>
          <div
            style={{
              display: "flex",
              height: dynamicBoxSize+"dvh",
              alignItems: "center",
              fontFamily: FONT,
              textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
            }}
          >
            <div>
              &nbsp; : <span style={{ color: COLOR_DIVISOR }}>{divisor}</span>
            </div>
          </div>
        </div>



        <div
          className="Aktion"
          style={{
            display: "flex",
            width: "30dvh",
            height: `${lineCount * dynamicBoxSize}dvh`,
            flexDirection: "column",
            marginTop: `${dynamicBoxSize / 2}dvh`,
            padding: "0.5dvh 0",
          }}
        >
          <div
            style={{
              display: "flex",
              height: dynamicBoxSize+"dvh",
              width: "30dvh",
              fontFamily: FONT,
              textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {levelTableDivisionAction}
          </div>
          {Array.from({ length: lineCount - 1 }).map((_, id) => (
            <div style={{opacity: id < lineCount-2 ? "0.5" : "1"}}>
              {solutionMiddle[id][0] === 0 && ( // Case: Action was dividing (with or without remainder).
                <div
                  style={{
                    display: "flex",
                    height: dynamicBoxSize+"dvh",
                    width: "30dvh",
                    fontFamily: FONT,
                    textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {solutionMiddle[id][1]} :&nbsp;
                  <span style={{ color: COLOR_DIVISOR }}>
                    {divisor}
                  </span>&nbsp;= {solutionMiddle[id][2]} {InputBoxRemainder}{" "}
                  {solutionMiddle[id][3]}
                </div>
              )}
              {solutionMiddle[id][0] === 1 && ( // Case: Action was an exchange of coins.
                <div
                  style={{
                    display: "flex",
                    height: dynamicBoxSize+"dvh",
                    width: "30dvh",
                    fontFamily: FONT,
                    textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {solutionMiddle[id][1]} x&nbsp;
                  <Coin
                    key={[id, 2]}
                    id={[id, 2]}
                    coinImage={coinImages[solutionMiddle[id][2]]}
                  />
                  &nbsp;= {solutionMiddle[id][2] % 2 === 0 ? solutionMiddle[id][1] * 2 : solutionMiddle[id][1] * 5} x&nbsp; {/* Depending on parity of current column (solutionMiddle[id][2]), set factor accordingly. */}
                  <Coin
                    key={[id, 3]}
                    id={[id, 3]}
                    coinImage={coinImages[solutionMiddle[id][2] + 1]}
                  />
                </div>
              )}
            </div>
          ))}
        </div>



        <div
          style={{
            display: "flex",
            height: `${
              (Math.min(lineCount, solutionLeft.length) + 2) * dynamicBoxSize
            }dvh`,
            width: "50dvh",
            justifyContent: "center",
            marginRight: "5dvh",
          }}
        >

          <div
            style={{
              display: "flex",
              fontFamily: FONT,
              textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.6)",
              height: dynamicBoxSize+"dvh",
              alignItems: "center",
            }}
          >
            <span style={{ color: COLOR_DIVISOR }}>{divisor}</span>&nbsp;x&nbsp;
          </div>
          <div
            className="table-division-table"
            style={{
              display: "flex",
              width: `${cols * dynamicBoxSize}dvh`,
              flexDirection: "column",
              boxShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
              borderRadius: "0.25em",
              position: "relative"
            }}
          >
            <div style={{position: "absolute", fontFamily: FONT, fontSize: "2dvh", textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.7)", left: "0.25dvh", top: "-2.5dvh"}}>
              {levelTableDivisionTitleRight}
            </div>

            <div
              style={{
                display: "flex",
                height: dynamicBoxSize+"dvh",
                width: `${cols * dynamicBoxSize}dvh`,
              }}
            >
              {Array.from({ length: cols }).map((_, id) => (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    ...getBorderStyle(0, id),
                    height: dynamicBoxSize+"dvh",
                    width: dynamicBoxSize+"dvh",
                  }}
                >
                  <Coin key={[id, 1]} id={[id, 1]} coinImage={coinImages[id]} />
                </div>
              ))}
            </div>
            <div
              style={{
                display: "flex",
                height: dynamicBoxSize+"dvh",
                width: `${cols * dynamicBoxSize}dvh`,
              }}
            >
              {Array.from({ length: cols }).map((_, id) => (
                <div style={{ ...inputStyle, ...getBorderStyle(0, id) }}>0</div>
              ))}
            </div>
            {Array.from({
              length: Math.min(lineCount, solutionLeft.length),
            }).map((_, id) => (
              <div
                style={{
                  display: "flex",
                  height: dynamicBoxSize+"dvh",
                  width: `${cols * dynamicBoxSize}dvh`,
                }}
              >
                {Array.from({ length: cols }).map((_, id2) => (
                  <input
                    className="input-field-table-division"
                    type="text"
                    maxLength={3}
                    value={inputsRight[id][id2]}
                    onChange={handleChangeRight(id, id2)}
                    style={{
                      ...inputStyle,
                      ...getBorderStyle(id + 1, id2, lineCount, 4),
                      backgroundColor: highlightedInput.every(
                        (v) => v[0] !== 1 || v[1] !== id || v[2] !== id2
                      )
                        ? undefined
                        : "rgb(252, 111, 111)",
                    }}
                    placeholder=""
                    pattern={REGEX_VALID_DECIMAL}
                    disabled={
                      id + 1 !== lineCount ||
                      lockedInputs.some(
                        (v) => v[0] === 1 && v[1] === id && v[2] === id2
                      ) ||
                      disableInput
                    }
                  ></input>
                ))}
              </div>
            ))}
          </div>
          {lineCount <= solutionLeft.length && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                fontFamily: FONT,
              }}
            >
              <button
                onClick={checkLine}
                className="checkLine-button"
                style={{ fontSize: lang === 2 ? (dynamicBoxSize/2.4)+"dvh": (dynamicBoxSize/2)+"dvh" }} // Scale down for french language.
                disabled={disableInput}
              >
                {" "}
                {buttonCheckLine}{" "}
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="input-box-table-division">
        <div style={{fontSize: (dividendLength > 9) ? (3.5/(dividendLength*0.1))+"dvh" : "3.5dvh"}}> {/* Scale fontSize if dividend contains more than 9 chars, inversely proportional to number of chars. */}
          <InputBox
          handleCorrect={handleCorrect}
          handleIncorrect={handleIncorrect}
          handleNoInput={handleNoInput}
          dividend={dividend}
          divisor={divisor}
          remainder={dividend % divisor}
          whichSystem={ROMAN_SYSTEM}
          showMessage={showMessage}
          disableInput={disableInput}
          ref={inputBoxRef}
        ></InputBox>
        </div>
      </div>
      <button
        onClick={callCheckInput}
        className="checkAnswer-button"
        disabled={disableInput}
      >
      {buttonCheck}
      </button>
    </div>
  );
}

export default Level5Roman;
